import React from 'react';
import { Box, Button, IconButton, Switch, Tooltip } from '@mui/material';
import { Edit, Trash2 } from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CatalogueAsset, CatalogueUnmapped, PaymentSettingCatalogue } from 'types/paymentSetting';
import { isCoinLiveOnProvider, liveNetworks } from './catalogueApi';
import EditableField from './EditableField';
import SortableRow from './SortableRow';
import styles from './Payments.module.scss';

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#00b3f5' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00b3f5' },
};

export const rowKey = (settingId: number, assetId: number) => `s${settingId}-a${assetId}`;

interface MethodsListProps {
  settings: PaymentSettingCatalogue[];
  /** Current (possibly dragged) asset-id order per setting id. */
  orderBySetting: Record<number, number[]>;
  onReorder: (settingId: number, assetIds: number[]) => void;
  onOpenNetworks: (settingId: number, assetId: number) => void;
  onToggleCoin: (setting: PaymentSettingCatalogue, asset: CatalogueAsset, checked: boolean) => void;
  onRenameAsset: (asset: CatalogueAsset, name: string) => Promise<void>;
  onUploadIcon: (asset: CatalogueAsset) => void;
  onDeleteIcon: (asset: CatalogueAsset) => void;
  onToggleUnmapped: (row: CatalogueUnmapped, checked: boolean) => void;
}

const IconPreview: React.FC<{ url: string | null }> = ({ url }) => (
  <Box className={styles.iconPreview}>
    {url ? (
      <img src={url} alt="icon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    ) : (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
        }}
      >
        ?
      </Box>
    )}
  </Box>
);

const MethodsList: React.FC<MethodsListProps> = ({
  settings,
  orderBySetting,
  onReorder,
  onOpenNetworks,
  onToggleCoin,
  onRenameAsset,
  onUploadIcon,
  onDeleteIcon,
  onToggleUnmapped,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (settingId: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderBySetting[settingId] ?? [];
    const from = ids.findIndex((id) => rowKey(settingId, id) === active.id);
    const to = ids.findIndex((id) => rowKey(settingId, id) === over.id);
    if (from < 0 || to < 0) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(settingId, next);
  };

  const unmapped = settings.flatMap((s) =>
    s.unmapped.map((row) => ({ row, paymentName: s.paymentName })),
  );

  return (
    <>
      <Box className={styles.tableWrapper}>
        <table className={styles.methodsTable}>
          <thead>
            <tr>
              <th style={{ width: 50 }}></th>
              <th style={{ width: 70 }}>ID</th>
              <th>Name</th>
              <th>Payment name</th>
              <th style={{ width: 160 }}>Networks</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 180 }}>Preview</th>
            </tr>
          </thead>
          {settings.map((s) => {
            const byId = new Map(s.assets.map((a) => [a.id, a]));
            const ids = (orderBySetting[s.paymentSettingId] ?? s.assets.map((a) => a.id)).filter(
              (id) => byId.has(id),
            );
            if (s.assets.length === 0) return null;
            return (
              <DndContext
                key={s.paymentSettingId}
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd(s.paymentSettingId)}
                accessibility={{ container: document.body }}
              >
                <SortableContext
                  items={ids.map((id) => rowKey(s.paymentSettingId, id))}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {ids.map((id) => {
                      const asset = byId.get(id) as CatalogueAsset;
                      return (
                        <SortableRow
                          key={rowKey(s.paymentSettingId, id)}
                          id={rowKey(s.paymentSettingId, id)}
                        >
                          <td>{asset.id}</td>
                          <td className={styles.nameCell}>
                            <EditableField
                              initialValue={asset.name}
                              onSave={(val) => onRenameAsset(asset, val)}
                            />
                          </td>
                          <td>
                            {s.paymentName}
                            {!s.isEnabled && (
                              <span className={styles.muted}> (setting disabled)</span>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="outlined"
                              size="small"
                              className={styles.networksBtn}
                              onClick={() => onOpenNetworks(s.paymentSettingId, asset.id)}
                            >
                              Networks ({asset.networks.length})
                            </Button>
                          </td>
                          <td>
                            {(() => {
                              const liveCount = liveNetworks(asset).length;
                              const total = asset.networks.length;
                              const live = isCoinLiveOnProvider(asset);
                              const partial = live && liveCount < total;
                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      live
                                        ? `Served by ${s.paymentName} — switch off to stop serving ${asset.code} through this provider`
                                        : `Switch on to serve ${asset.code} through ${s.paymentName}`
                                    }
                                  >
                                    <Switch
                                      size="small"
                                      checked={live}
                                      onChange={(_, c) => onToggleCoin(s, asset, c)}
                                      sx={switchSx}
                                      inputProps={{
                                        'aria-label': `${asset.code} on ${s.paymentName}`,
                                      }}
                                    />
                                  </Tooltip>
                                  {partial && (
                                    <span className={styles.muted}>
                                      {liveCount}/{total} live
                                    </span>
                                  )}
                                  {!asset.isEnabled && (
                                    <span className={styles.muted}>coin disabled</span>
                                  )}
                                </Box>
                              );
                            })()}
                          </td>
                          <td>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                              }}
                            >
                              <IconPreview url={asset.imageUrl} />
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Upload coin icon (shown to players)">
                                  <IconButton
                                    size="small"
                                    aria-label="Upload coin icon"
                                    onClick={() => onUploadIcon(asset)}
                                    sx={{ color: 'var(--text-secondary)' }}
                                  >
                                    <Edit size={16} />
                                  </IconButton>
                                </Tooltip>
                                {asset.imageUrl && (
                                  <Tooltip title="Remove coin icon">
                                    <IconButton
                                      size="small"
                                      aria-label="Remove coin icon"
                                      onClick={() => onDeleteIcon(asset)}
                                      sx={{ color: '#f44336' }}
                                    >
                                      <Trash2 size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </td>
                        </SortableRow>
                      );
                    })}
                  </tbody>
                </SortableContext>
              </DndContext>
            );
          })}
        </table>
      </Box>

      {unmapped.length > 0 && (
        <>
          <div className={styles.unmappedTitle}>
            Unmapped rows — no network, not visible to players
          </div>
          <Box className={styles.tableWrapper}>
            <table className={styles.methodsTable}>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>ID</th>
                  <th>Name</th>
                  <th>Payment name</th>
                  <th style={{ width: 100 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {unmapped.map(({ row, paymentName }) => (
                  <tr key={`u${row.id}`}>
                    <td>{row.id}</td>
                    <td className={styles.nameCell}>
                      {row.name} <span className={styles.muted}>({row.code})</span>
                    </td>
                    <td>{paymentName}</td>
                    <td>
                      <Switch
                        size="small"
                        checked={row.isEnabled}
                        onChange={(_, c) => onToggleUnmapped(row, c)}
                        sx={switchSx}
                        inputProps={{ 'aria-label': `${row.code} enabled` }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      )}
    </>
  );
};

export default MethodsList;
